import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Dropdown, Button, Form } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { getEventById } from '../../lib/api/events';
import { deleteApplication, getApplicationsByEventDecrypted, updateApplicationStatus, exportApplicationsToCSV } from '../../lib/api/applications';
import { resendAdminApplicationNotification } from '../../lib/api/notifications';
import { getSurveysForApplications, sendPostEventSurvey, sendPreEventSurvey } from '../../lib/api/surveys';
import ApplicationDetailModal from '../../components/ApplicationDetailModal';
import type { EventWithDetails, ApplicationWithDetails, PostEventSurvey, SurveyKey } from '../../types/database';
import { captureError } from '../../lib/capture-error';
import {
  NO_OPTION,
  buildApplicationsPrintUrl,
  buildGroupedByChurch,
  buildGroupedByOption,
  filterApplications,
  formatOptionLabel,
  getSelectedOptionsForApp,
} from '../../lib/applications-report';
import './AdminApplicationsPage.css';

const AdminApplicationsPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [event, setEvent] = useState<EventWithDetails | null>(null);
  const [applications, setApplications] = useState<ApplicationWithDetails[]>([]);
  const [filteredApplications, setFilteredApplications] = useState<ApplicationWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [groupByChurch, setGroupByChurch] = useState(false);
  const [groupByOption, setGroupByOption] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<ApplicationWithDetails | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [surveysByApplication, setSurveysByApplication] = useState<Record<string, Partial<Record<SurveyKey, PostEventSurvey>>>>({});
  const [resendingApplicationId, setResendingApplicationId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    if (!eventId) return;

    try {
      setLoading(true);
      const [eventData, applicationsData] = await Promise.all([
        getEventById(eventId),
        getApplicationsByEventDecrypted(eventId),
      ]);

      setEvent(eventData);
      setApplications(applicationsData);
      const surveys = await getSurveysForApplications(applicationsData.map((app) => app.id));
      const groupedSurveys: Record<string, Partial<Record<SurveyKey, PostEventSurvey>>> = {};
      for (const survey of surveys) {
        if (!groupedSurveys[survey.event_application_id]) {
          groupedSurveys[survey.event_application_id] = {};
        }
        groupedSurveys[survey.event_application_id][survey.survey_key] = survey;
      }
      setSurveysByApplication(groupedSurveys);
    } catch (err) {
        setError('Failed to load applications');
        captureError(err);
      } finally {
      setLoading(false);
    }
  }, [eventId]);

  const applyFilters = useCallback(() => {
    setFilteredApplications(
      filterApplications(applications, { status: statusFilter, searchQuery })
    );
  }, [applications, statusFilter, searchQuery]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const signupOptions = useMemo(() => event?.signup_options ?? [], [event?.signup_options]);
  const hasSignupOptions = signupOptions.length > 0;
  const optionById = useMemo(
    () => new Map(signupOptions.map((option) => [option.id, option])),
    [signupOptions]
  );

  const groupedByChurch = useMemo(
    () => buildGroupedByChurch(filteredApplications, groupByChurch, groupByOption),
    [filteredApplications, groupByChurch, groupByOption]
  );

  const groupedByOption = useMemo(
    () => buildGroupedByOption(filteredApplications, signupOptions, groupByOption, groupByChurch),
    [filteredApplications, signupOptions, groupByOption, groupByChurch]
  );

  function handleOpenPrintReport() {
    if (!eventId) return;
    const url = buildApplicationsPrintUrl(eventId, {
      status: statusFilter,
      searchQuery,
      groupByChurch,
      groupByOption,
    });
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  async function handleStatusUpdate(applicationId: string, newStatus: string, notes?: string) {
    try {
      await updateApplicationStatus(applicationId, newStatus, notes, user?.id);
      // Reload applications
      await loadData();
      showToast('Status updated successfully!', 'success');
    } catch (err) {
      showToast('Failed to update status', 'error');
      captureError(err);
    }
  }

  function handleViewDetails(application: ApplicationWithDetails) {
    setSelectedApplication(application);
    setShowDetailModal(true);
  }

  function handleExportCSV() {
    const csv = exportApplicationsToCSV(filteredApplications);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${event?.slug || 'event'}-applications-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function getStatusBadge(status: string) {
    const statusClass = {
      draft: 'bg-secondary',
      submitted: 'bg-info',
      under_review: 'bg-warning',
      approved: 'bg-success',
      rejected: 'bg-danger',
      waitlisted: 'bg-warning',
    }[status] || 'bg-secondary';

    return <span className={`badge ${statusClass}`}>{status.replace('_', ' ')}</span>;
  }

  function getSurveyState(survey?: PostEventSurvey): 'not_sent' | 'sent_not_submitted' | 'submitted' {
    if (!survey || !survey.sent_at) return 'not_sent';
    if (survey.submitted_at) return 'submitted';
    return 'sent_not_submitted';
  }

  function getSurveyLabel(surveyKey: SurveyKey): string {
    return surveyKey === 'pre_event' ? 'Pre-Event' : 'Post-Event';
  }

  async function handleSendSurvey(applicationId: string, surveyKey: SurveyKey) {
    try {
      if (surveyKey === 'pre_event') {
        await sendPreEventSurvey(applicationId);
      } else {
        await sendPostEventSurvey(applicationId);
      }
      await loadData();
      showToast(`${getSurveyLabel(surveyKey)} survey sent`, 'success');
    } catch (err) {
      captureError(err, 'Failed to send survey');
      showToast(err instanceof Error ? err.message : 'Failed to send survey', 'error');
    }
  }

  async function handleResendNotification(application: ApplicationWithDetails) {
    if (!eventId || application.status === 'draft') return;

    setResendingApplicationId(application.id);
    try {
      const result = await resendAdminApplicationNotification(eventId, application.id);
      if (result.success) {
        showToast(result.message, 'success');
      } else {
        showToast(result.message, 'error');
      }
    } catch (err) {
      captureError(err, 'Failed to resend admin notification');
      showToast('Failed to resend admin notification', 'error');
    } finally {
      setResendingApplicationId(null);
    }
  }

  async function handleDeleteApplication(application: ApplicationWithDetails) {
    if (application.status !== 'rejected') return;

    const name = `${application.attendee_profile?.first_name || ''} ${application.attendee_profile?.last_name || ''}`.trim() || 'this applicant';
    const eventTitle = event?.title || 'this event';
    const confirmed = window.confirm(
      `Delete the rejected application for ${name} (${eventTitle})?\n\nThe attendee profile will be kept so they can apply again.`
    );
    if (!confirmed) return;

    try {
      await deleteApplication(application.id);
      if (selectedApplication?.id === application.id) {
        setShowDetailModal(false);
        setSelectedApplication(null);
      }
      await loadData();
      showToast('Application deleted', 'success');
    } catch (err) {
      captureError(err, 'Failed to delete application');
      showToast(err instanceof Error ? err.message : 'Failed to delete application', 'error');
    }
  }

  function renderSelectedOptions(app: ApplicationWithDetails) {
    const selectedOptions = getSelectedOptionsForApp(app, optionById);
    if (selectedOptions.length === 0) {
      return <span className="text-muted">—</span>;
    }

    return (
      <div className="d-flex flex-column gap-1">
        {selectedOptions.map((option) => (
          <span key={option.id} className="badge bg-light text-dark text-start">
            {formatOptionLabel(option)}
          </span>
        ))}
      </div>
    );
  }

  function renderApplicationsTable(applications: ApplicationWithDetails[]) {
    return (
      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th>Applicant</th>
                <th>Contact</th>
                {hasSignupOptions && <th>Options</th>}
                <th>Status</th>
                <th>Submitted</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {applications.map(renderApplicationRow)}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  function renderApplicationRow(app: ApplicationWithDetails) {
    const profile = app.attendee_profile;
    const appSurveys = surveysByApplication[app.id] || {};
    const preSurvey = appSurveys.pre_event;
    const postSurvey = appSurveys.post_event;
    const preState = getSurveyState(preSurvey);
    const postState = getSurveyState(postSurvey);
    return (
      <tr key={app.id}>
        <td>
          <strong>
            {profile?.first_name} {profile?.last_name}
          </strong>
          <br />
          <small className="text-muted">ID: {app.id.slice(0, 8)}</small>
        </td>
        <td>
          <small>
            {profile?.email}
            <br />
            {profile?.cell_phone}
          </small>
        </td>
        {hasSignupOptions && <td>{renderSelectedOptions(app)}</td>}
        <td>
          {getStatusBadge(app.status)}
          <div className="mt-1 d-flex gap-1 flex-wrap">
            <span className={`badge ${preState === 'submitted' ? 'bg-success' : preState === 'sent_not_submitted' ? 'bg-warning text-dark' : 'bg-light text-dark'}`}>
              Pre: {preState === 'not_sent' ? 'Not Sent' : preState === 'submitted' ? 'Submitted' : 'Sent'}
            </span>
            <span className={`badge ${postState === 'submitted' ? 'bg-success' : postState === 'sent_not_submitted' ? 'bg-warning text-dark' : 'bg-light text-dark'}`}>
              Post: {postState === 'not_sent' ? 'Not Sent' : postState === 'submitted' ? 'Submitted' : 'Sent'}
            </span>
          </div>
        </td>
        <td>
          <small>
            {app.submitted_at
              ? new Date(app.submitted_at).toLocaleString()
              : '—'}
          </small>
        </td>
        <td className="text-end">
          <div className="d-flex gap-2 justify-content-end">
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => handleViewDetails(app)}
              title="View full application details"
            >
              <i className="fas fa-eye me-1"></i>
              View Details
            </Button>
            <Dropdown>
              <Dropdown.Toggle
                variant="outline-secondary"
                size="sm"
                id={`app-actions-${app.id}`}
              >
                <i className="fas fa-ellipsis-v"></i>
              </Dropdown.Toggle>
              <Dropdown.Menu align="end" popperConfig={{ strategy: 'fixed' }}>
                {app.status === 'submitted' && (
                  <>
                    <Dropdown.Item onClick={() => handleStatusUpdate(app.id, 'under_review')}>
                      <i className="fas fa-eye me-2"></i>
                      Mark as Under Review
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleStatusUpdate(app.id, 'approved')}>
                      <i className="fas fa-check me-2"></i>
                      Approve
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleStatusUpdate(app.id, 'rejected')}>
                      <i className="fas fa-times me-2"></i>
                      Reject
                    </Dropdown.Item>
                  </>
                )}
                {app.status === 'under_review' && (
                  <>
                    <Dropdown.Item onClick={() => handleStatusUpdate(app.id, 'approved')}>
                      <i className="fas fa-check me-2"></i>
                      Approve
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleStatusUpdate(app.id, 'waitlisted')}>
                      <i className="fas fa-clock me-2"></i>
                      Waitlist
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => handleStatusUpdate(app.id, 'rejected')}>
                      <i className="fas fa-times me-2"></i>
                      Reject
                    </Dropdown.Item>
                  </>
                )}
                {(app.status === 'approved' || app.status === 'rejected' || app.status === 'waitlisted') && (
                  <Dropdown.Item onClick={() => handleStatusUpdate(app.id, 'submitted')}>
                    <i className="fas fa-undo me-2"></i>
                    Reset to Submitted
                  </Dropdown.Item>
                )}
                {app.status !== 'draft' && (
                  <>
                    <Dropdown.Divider />
                    <Dropdown.Item
                      onClick={() => handleResendNotification(app)}
                      disabled={resendingApplicationId === app.id}
                    >
                      <i className="fas fa-envelope me-2"></i>
                      {resendingApplicationId === app.id ? 'Sending...' : 'Resend Admin Notification'}
                    </Dropdown.Item>
                  </>
                )}
                {app.status === 'rejected' && (
                  <Dropdown.Item
                    onClick={() => handleDeleteApplication(app)}
                    className="text-danger"
                  >
                    <i className="fas fa-trash me-2"></i>
                    Delete Application
                  </Dropdown.Item>
                )}
                {app.status === 'approved' && (
                  <>
                    <Dropdown.Divider />
                    <Dropdown.Item
                      onClick={() => handleSendSurvey(app.id, 'pre_event')}
                      disabled={preState === 'submitted'}
                    >
                      <i className="fas fa-paper-plane me-2"></i>
                      {preState === 'not_sent' ? 'Send Pre-Event Survey' : preState === 'sent_not_submitted' ? 'Resend Pre-Event Survey' : 'Pre-Event Survey Submitted'}
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => handleSendSurvey(app.id, 'post_event')}
                      disabled={postState === 'submitted'}
                    >
                      <i className="fas fa-paper-plane me-2"></i>
                      {postState === 'not_sent' ? 'Send Post-Event Survey' : postState === 'sent_not_submitted' ? 'Resend Post-Event Survey' : 'Post-Event Survey Submitted'}
                    </Dropdown.Item>
                  </>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </td>
      </tr>
    );
  }

  if (loading) {
    return (
      <div className="container my-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="container my-5">
        <div className="alert alert-danger" role="alert">
          Event not found
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid my-5">
      <div className="row mb-4">
        <div className="col">
          <h1>Applications for {event.title}</h1>
          <p className="text-muted">
            Total: {applications.length} | Filtered: {filteredApplications.length}
          </p>
        </div>
        <div className="col-auto">
          <Link to="/admin" className="btn btn-outline-secondary me-2">
            <i className="fas fa-arrow-left me-2"></i>
            Back to Events
          </Link>
          <button
            type="button"
            onClick={handleOpenPrintReport}
            className="btn btn-outline-primary me-2"
            disabled={filteredApplications.length === 0}
          >
            <i className="fas fa-print me-2"></i>
            Print report
          </button>
          <button onClick={handleExportCSV} className="btn btn-success" disabled={filteredApplications.length === 0}>
            <i className="fas fa-download me-2"></i>
            Export CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Status Filter</label>
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="submitted">Submitted</option>
                <option value="under_review">Under Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="waitlisted">Waitlisted</option>
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Search</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search by name, email, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Display</label>
              <div className="mt-2 d-flex flex-column gap-2">
                {hasSignupOptions && (
                  <Form.Check
                    type="switch"
                    id="group-by-option"
                    label="Group by Option"
                    checked={groupByOption}
                    onChange={(e) => setGroupByOption(e.target.checked)}
                  />
                )}
                <Form.Check
                  type="switch"
                  id="group-by-church"
                  label="Group by Church"
                  checked={groupByChurch}
                  onChange={(e) => setGroupByChurch(e.target.checked)}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Applications Table */}
      {filteredApplications.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
            <h4>No Applications Found</h4>
            <p className="text-muted">
              {applications.length === 0
                ? 'No one has applied to this event yet.'
                : 'Try adjusting your filters.'}
            </p>
          </div>
        </div>
      ) : groupByOption && groupedByOption ? (
        <div className="option-groups">
          {groupedByOption.map((optionGroup) => (
            <div key={optionGroup.optionId ?? NO_OPTION} className="option-group mb-4">
              <div className="option-group-header">
                <h5 className="mb-0">
                  <i className="fas fa-calendar-alt me-2"></i>
                  {optionGroup.optionLabel}
                  <span className="badge bg-secondary ms-2">{optionGroup.applications.length}</span>
                </h5>
              </div>
              {groupByChurch && optionGroup.churchGroups ? (
                <div className="option-group-body">
                  {optionGroup.churchGroups.map((churchGroup) => (
                    <div key={churchGroup.churchName} className="church-subgroup mb-3">
                      <div className="church-subgroup-header">
                        <h6 className="mb-0">
                          <i className="fas fa-church me-2"></i>
                          {churchGroup.churchName}
                          <span className="badge bg-secondary ms-2">{churchGroup.applications.length}</span>
                        </h6>
                      </div>
                      {renderApplicationsTable(churchGroup.applications)}
                    </div>
                  ))}
                </div>
              ) : (
                renderApplicationsTable(optionGroup.applications)
              )}
            </div>
          ))}
        </div>
      ) : groupByChurch && groupedByChurch ? (
        /* Grouped by Church View */
        <div className="church-groups">
          {groupedByChurch.map((group) => (
            <div key={group.churchName} className="church-group mb-4">
              <div className="church-group-header">
                <h5 className="mb-0">
                  <i className="fas fa-church me-2"></i>
                  {group.churchName}
                  <span className="badge bg-secondary ms-2">{group.applications.length}</span>
                </h5>
              </div>
              {renderApplicationsTable(group.applications)}
            </div>
          ))}
        </div>
      ) : (
        /* Flat View */
        renderApplicationsTable(filteredApplications)
      )}

      {/* Application Detail Modal */}
      <ApplicationDetailModal
        application={selectedApplication}
        surveys={selectedApplication ? surveysByApplication[selectedApplication.id] : undefined}
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        onStatusUpdate={handleStatusUpdate}
        onResendNotification={
          selectedApplication && eventId
            ? () => handleResendNotification(selectedApplication)
            : undefined
        }
        resendingNotification={selectedApplication ? resendingApplicationId === selectedApplication.id : false}
        onDeleteApplication={
          selectedApplication?.status === 'rejected'
            ? () => handleDeleteApplication(selectedApplication)
            : undefined
        }
        eventTitle={event?.title}
      />
    </div>
  );
};

export default AdminApplicationsPage;
